import axios from 'axios';
import { User, Message, Stream, AuthResponse, AdReward } from '../types';

const isProd = process.env.NODE_ENV === 'production' || window.location.hostname === '207.154.195.11';
export const BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://207.154.195.11:5000';
const API_BASE_URL = `${BASE_URL}/api`;

export const getImageUrl = (path?: string): string => {
  if (!path) return 'https://via.placeholder.com/150';
  const normalizedPath = path.replace(/\\/g, '/');
  if (normalizedPath.startsWith('http') || normalizedPath.startsWith('data:')) return normalizedPath;
  if (normalizedPath.startsWith('/')) return `${BASE_URL}${normalizedPath}`;
  return `${BASE_URL}/${normalizedPath}`;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Bypass-Tunnel-Reminder': 'true'
  }
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: async (userData: { username: string; email: string; password: string }): Promise<AuthResponse> => {
    const response = await api.post('/register', userData);
    return response.data;
  },

  login: async (credentials: { email: string; password: string }): Promise<AuthResponse> => {
    const response = await api.post('/login', credentials);
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response = await api.get('/user/profile');
    return response.data;
  },
};

export const userAPI = {
  getUsers: async (search?: string): Promise<User[]> => {
    const params = search ? { search } : {};
    const response = await api.get('/users', { params });
    return response.data;
  },

  uploadProfileImage: async (file: File): Promise<{ message: string; profileImage: string }> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/upload/profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getUserProfile: async (userId: string): Promise<any> => {
    const response = await api.get(`/user/${userId}`);
    return response.data;
  },

  updateProfile: async (data: { username?: string; bio?: string }): Promise<{ message: string; user: User }> => {
    const response = await api.put('/user/profile', data);
    return response.data;
  },

  addCoins: async (amount: number): Promise<{ message: string; user: User }> => {
    const response = await api.post('/user/add-coins', { amount });
    return response.data;
  },

  purchaseVip: async (): Promise<{ message: string; user: User }> => {
    const response = await api.post('/user/vip-purchase');
    return response.data;
  }
};

export const postsAPI = {
  createPost: async (file: File, caption?: string): Promise<{ message: string; post: any }> => {
    const formData = new FormData();
    formData.append('image', file);
    if (caption) {
      formData.append('caption', caption);
    }
    const response = await api.post('/posts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  getPosts: async (userId: string): Promise<any[]> => {
    const response = await api.get(`/posts/${userId}`);
    return response.data;
  },

  likePost: async (postId: string): Promise<{ message: string; post: any }> => {
    const response = await api.post(`/posts/${postId}/like`);
    return response.data;
  },

  addComment: async (postId: string, text: string): Promise<{ message: string; post: any }> => {
    const response = await api.post(`/posts/${postId}/comment`, { text });
    return response.data;
  },

  deletePost: async (postId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/posts/${postId}`);
    return response.data;
  }
};

export const friendsAPI = {
  sendRequest: async (friendId: string): Promise<{ message: string }> => {
    const response = await api.post('/friends/request', { friendId });
    return response.data;
  },

  getFriends: async (): Promise<User[]> => {
    const response = await api.get('/friends');
    return response.data;
  },

  removeFriend: async (friendId: string): Promise<{ message: string }> => {
    const response = await api.post('/friends/remove', { friendId });
    return response.data;
  },
};

export const messagesAPI = {
  getMessages: async (userId: string): Promise<Message[]> => {
    const response = await api.get(`/messages/${userId}`);
    return response.data;
  },

  sendMessage: async (messageData: { receiver: string; content: string }): Promise<Message> => {
    const response = await api.post('/messages', messageData);
    return response.data;
  },
};

export const streamAPI = {
  startStream: async (title: string): Promise<{ message: string; stream: Stream }> => {
    const response = await api.post('/stream/start', { title });
    return response.data;
  },

  stopStream: async (): Promise<{ message: string }> => {
    const response = await api.post('/stream/stop');
    return response.data;
  },

  getStreams: async (): Promise<Stream[]> => {
    const response = await api.get('/streams');
    return response.data;
  },
};

export const adAPI = {
  watchAd: async (): Promise<{ message: string; coinsEarned: number; totalCoins: number; adsWatchedToday: number; adsUntilReward?: number }> => {
    const response = await api.post('/ads/watch');
    return response.data;
  },
  getAdStatus: async (): Promise<{ adsWatchedToday: number; coinsEarned: number }> => {
    const response = await api.get('/ads/status');
    return response.data;
  },
};

export const notificationAPI = {
  getNotifications: async (): Promise<any[]> => {
    const response = await api.get('/notifications');
    return response.data;
  },
  
  markAsRead: async (): Promise<{ message: string }> => {
    const response = await api.post('/notifications/read');
    return response.data;
  }
};

export default api;
